import React, { useCallback, useEffect, useRef, useState } from "react";

function ConfirmDialog({
  title,
  message,
  subject,
  consequences = [],
  confirmLabel = "Delete",
  tone = "danger",
  onResolve,
}) {
  const cancelRef = useRef(null);
  const dialogRef = useRef(null);

  // Destructive by default, so focus starts on Cancel; Escape cancels.
  useEffect(() => {
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onResolve(false);
      }
      // Keep Tab cycling inside the dialog.
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll("button");
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onResolve]);

  return (
    <div
      className="tk-modal-backdrop tk-confirm-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onResolve(false)}
    >
      <div
        ref={dialogRef}
        className={`tk-confirm tk-confirm-${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="tk-confirm-title"
        aria-describedby="tk-confirm-message"
      >
        <div className="tk-confirm-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
          </svg>
        </div>
        <h2 id="tk-confirm-title">{title}</h2>
        {subject && <div className="tk-confirm-subject">{subject}</div>}
        {message && (
          <p id="tk-confirm-message" className="tk-confirm-message">
            {message}
          </p>
        )}
        {consequences.length > 0 && (
          <ul className="tk-confirm-list">
            {consequences.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}
        <div className="tk-confirm-actions">
          <button
            ref={cancelRef}
            type="button"
            className="btn-secondary"
            onClick={() => onResolve(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={tone === "danger" ? "btn-danger" : "btn-primary"}
            onClick={() => onResolve(true)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Styled replacement for window.confirm:
//   const [confirm, confirmDialog] = useConfirm();
//   if (await confirm({ title, ... })) { ... }
// and render {confirmDialog} somewhere in the component.
export default function useConfirm() {
  const [request, setRequest] = useState(null);

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setRequest({ ...options, resolve });
      }),
    [],
  );

  const onResolve = useCallback((value) => {
    setRequest((current) => {
      current?.resolve(value);
      return null;
    });
  }, []);

  const dialog = request ? (
    <ConfirmDialog {...request} onResolve={onResolve} />
  ) : null;
  return [confirm, dialog];
}
